import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AssessmentApi, Configuration, QuestionsApi, RoleApi, SkillApi, TopicApi, UserRoleApi } from './apis';
import { FetchStatus, FetchType } from './enums/fetch';

export interface FetchStateItem {
  status: FetchStatus;
  type?: FetchType;
}

export const DefaultOptions = { 'credentials': 'include' };

interface GlobalState {
  apis: {
    roleApi: RoleApi;
    assessmentApi: AssessmentApi;
    questionsApi: QuestionsApi;
    skillApi: SkillApi;
    topicApi: TopicApi;
    userRoleApi: UserRoleApi;
  };
  fetchState: FetchStateItem;
  [key: string]: any;
  isLoading: boolean;
  // side-nav
  mayViewSideNav: boolean;
  sideNavCollapsed: boolean;
  sideNavVisible: boolean;
}

interface GlobalContextProps {
  state: GlobalState;
  setGlobalState: (key: string, value: any) => void;
  setFetchStatus: (status: FetchStatus, type?: FetchType) => void;
  setIsLoading: (isLoading: boolean) => void;
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined);

export const GlobalProvider = ({ children }: { children: ReactNode }) => {
  const config = new Configuration({
    basePath: process.env.REACT_APP_KEPLER_API_ENDPOINT as string,
    apiKey: 'your-api-key', 
  });

  const apis = {
    roleApi: new RoleApi(config),
    assessmentApi: new AssessmentApi(config),
    questionsApi: new QuestionsApi(config),
    skillApi: new SkillApi(config),
    topicApi: new TopicApi(config),
    userRoleApi: new UserRoleApi(config),
  };

  const fetchState = {
    status: FetchStatus.Inactive,
  };
  const [state, setState] = useState<GlobalState>({
    apis,
    fetchState,
    isLoading: false,
    sideNavCollapsed: true,
    sideNavVisible: true,
    mayViewSideNav: true
  });

  const setIsLoading = (isLoading: boolean) => {
    setState(prevState => ({ ...prevState, isLoading: isLoading }));
  };

  const setGlobalState = (key: string, value: any) => {
    setState(prevState => ({ ...prevState, [key]: value }));
  };

  const setFetchStatus = (status: FetchStatus, type?: FetchType) => {
    const fetchState = {
      status: status,
      type: type
    };
    setState(prevState => ({ ...prevState, fetchState: fetchState }));
  };

  return (
    <GlobalContext.Provider value={{ state, setGlobalState, setFetchStatus, setIsLoading }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalState = (key: string) => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalState must be used within a GlobalProvider');
  }
  return {
    value: context.state[key],
    setValue: (value: any) => context.setGlobalState(key, value),
  };
};

export const useApis = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useApis must be used within a GlobalProvider');
  }
  return context.state.apis;
};

export const useSetLoading = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useSetLoading must be used within a GlobalProvider');
  }
  return context.setIsLoading;
};

export const useSetFetchState= () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('setFetchStatus must be used within a GlobalProvider');
  }
  return context.setFetchStatus;
};


export const useFetchState = (): FetchStateItem => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useFetchState must be used within a GlobalProvider');
  }
  return context.state.fetchState;
};

export const useLoading = (): boolean => {
  const context = useContext(GlobalContext);
  return context?.state.isLoading!;
}

export default GlobalProvider;
